import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMenu } from '../../context/MenuContext';
import MenuFormWithUpload from './MenuFormWithUpload'; // ✅ Updated import
import MenuSwapModal from './MenuSwapModal';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiTrash2, FiPlus, FiChevronDown, FiChevronUp, FiCopy, FiArrowRight, FiRefreshCw, FiCalendar, FiMove } = FiIcons;

const NextWeekMenus = () => {
  const { 
    nextWeeklyMenus, 
    updateMenu, 
    createMenu, 
    copyCurrentToNextWeek, 
    moveNextWeekToCurrent, 
    fetchNextWeeklyMenus, 
    selectedMenu, 
    setSelectedMenu, 
    swapMenuDays 
  } = useMenu();
  
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [swapSourceMenu, setSwapSourceMenu] = useState(null);
  const [draggedMenu, setDraggedMenu] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);

  const handleEdit = (menu) => {
    setSelectedMenu(menu);
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleCreate = () => {
    setSelectedMenu(null);
    setIsEditing(false);
    setIsCreating(true);
  };

  const handleCancel = () => {
    setSelectedMenu(null);
    setIsEditing(false);
    setIsCreating(false);
  };

  const toggleExpand = (id) => {
    setExpandedMenus(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const refreshMenus = async () => {
    setRefreshing(true);
    try {
      await fetchNextWeeklyMenus();
    } catch (error) {
      console.error('Error refreshing menus:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSubmit = async (formData) => {
    setIsLoading(true);
    try {
      console.log('Submitting next week menu form:', { isEditing, formData });

      if (isEditing && selectedMenu) {
        console.log('Updating next week menu:', selectedMenu.id, formData);
        await updateMenu(selectedMenu.id, formData, true); // true for next week
        console.log('Next week menu updated successfully');
      } else if (isCreating) {
        console.log('Creating new next week menu:', formData);
        await createMenu(formData, true); // true for next week
        console.log('Next week menu created successfully');
      }

      setSelectedMenu(null);
      setIsEditing(false);
      setIsCreating(false);

      // Refresh menus to ensure UI is up-to-date
      await fetchNextWeeklyMenus();
    } catch (error) {
      console.error('Error saving next week menu:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyCurrentToNext = async () => {
    setIsCopying(true);
    try {
      await copyCurrentToNextWeek();
    } catch (error) {
      console.error('Error copying menus:', error);
    } finally {
      setIsCopying(false);
    }
  };

  const handleMoveToCurrentWeek = async () => {
    setIsMoving(true);
    try {
      await moveNextWeekToCurrent();
    } catch (error) {
      console.error('Error moving menus:', error);
    } finally {
      setIsMoving(false);
    }
  };

  // Menü-Tausch-Funktionen
  const handleSwapMenus = (sourceMenu) => {
    setSwapSourceMenu(sourceMenu);
    setSwapModalOpen(true);
  };

  const executeSwap = async (targetDay) => {
    if (!swapSourceMenu) return;

    try {
      setIsLoading(true);
      await swapMenuDays(swapSourceMenu.day, targetDay, true); // true für nächste Woche
      setSwapModalOpen(false);
      setSwapSourceMenu(null);
      await fetchNextWeeklyMenus(); // UI aktualisieren
    } catch (error) {
      console.error('Error swapping menus:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Drag and Drop Funktionen
  const handleDragStart = (e, menu) => {
    setDraggedMenu(menu);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(day);
  };

  const handleDragLeave = () => {
    setDragOverDay(null);
  };

  const handleDrop = async (e, targetDay) => {
    e.preventDefault();
    setDragOverDay(null);

    if (!draggedMenu || draggedMenu.day === targetDay) {
      setDraggedMenu(null);
      return;
    }

    try {
      setIsLoading(true);
      await swapMenuDays(draggedMenu.day, targetDay, true);
      await fetchNextWeeklyMenus();
    } catch (error) {
      console.error('Error swapping menus via drag and drop:', error);
    } finally {
      setDraggedMenu(null);
      setIsLoading(false);
    }
  };

  // KORRIGIERTE SORTIERUNG: Sortiere Menüs nach den Tagen in der richtigen Reihenfolge
  const getSortedMenus = () => {
    const dayOrder = { 'dienstag': 1, 'mittwoch': 2, 'donnerstag': 3, 'freitag': 4 };
    return Object.values(nextWeeklyMenus).sort((a, b) => {
      const orderA = dayOrder[a.day?.toLowerCase()] || 999;
      const orderB = dayOrder[b.day?.toLowerCase()] || 999;
      return orderA - orderB;
    });
  };

  const sortedMenus = getSortedMenus();

  // FESTE TAGE-REIHENFOLGE - Immer in korrekter Reihenfolge
  const allDays = ['dienstag', 'mittwoch', 'donnerstag', 'freitag'];

  return (
    <div>
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between">
        <div className="mb-4 md:mb-0">
          <h1 className="text-3xl font-bold text-gray-800">Menüs nächste Woche</h1>
          <p className="text-gray-600">Planen Sie die Menüs für die kommende Woche</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <motion.button
            onClick={refreshMenus}
            disabled={refreshing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-200 transition-colors duration-300 flex items-center justify-center space-x-2 ${
              refreshing ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            <SafeIcon icon={FiRefreshCw} className={refreshing ? 'animate-spin' : ''} />
            <span>Aktualisieren</span>
          </motion.button>

          <motion.button
            onClick={handleCopyCurrentToNext}
            disabled={isCopying}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`bg-blue-500 text-white px-4 py-2 rounded-xl font-semibold hover:bg-blue-600 transition-colors duration-300 flex items-center justify-center space-x-2 ${
              isCopying ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            <SafeIcon icon={FiCopy} />
            <span>Aktuelle Menüs kopieren</span>
          </motion.button>

          <motion.button
            onClick={handleCreate}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-primary-500 text-white px-4 py-2 rounded-xl font-semibold hover:bg-primary-600 transition-colors duration-300 flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiPlus} />
            <span>Neues Menü</span>
          </motion.button>
        </div>
      </div>

      {/* Move to current week button */}
      {Object.values(nextWeeklyMenus).length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center justify-between"
        >
          <div>
            <h3 className="text-lg font-semibold text-green-800">Bereit für die nächste Woche?</h3>
            <p className="text-green-700">
              Verschieben Sie die geplanten Menüs in die aktuelle Woche.
            </p>
          </div>
          <motion.button
            onClick={handleMoveToCurrentWeek}
            disabled={isMoving}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`bg-green-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-green-700 transition-colors duration-300 flex items-center space-x-2 ${
              isMoving ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            <span>Als aktuelle Woche setzen</span>
            <SafeIcon icon={FiArrowRight} />
          </motion.button>
        </motion.div>
      )}

      {/* Sortierungs-Info */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4"
      >
        <div className="flex items-center space-x-2 text-blue-800">
          <SafeIcon icon={FiCalendar} />
          <span className="font-medium">Automatische Sortierung:</span>
        </div>
        <p className="text-blue-700 text-sm mt-1">
          Menüs werden immer in der Reihenfolge Dienstag → Mittwoch → Donnerstag → Freitag angezeigt, unabhängig von Backend-Änderungen.
        </p>
      </motion.div>

      {/* Drag and Drop Info */}
      {sortedMenus.length > 1 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <div className="flex items-center space-x-2 text-green-800">
            <SafeIcon icon={FiMove} />
            <span className="font-medium">Menüs verschieben:</span>
          </div>
          <p className="text-green-700 text-sm mt-1">
            Ziehen Sie Menüs per Drag & Drop zwischen den Tagen oder nutzen Sie die Tausch-Buttons.
          </p>
        </motion.div>
      )}

      {/* Form for editing/creating */}
      {(isEditing || isCreating) && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-md p-6 mb-8"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            {isEditing ? 'Menü bearbeiten' : 'Neues Menü erstellen'}
          </h2>
          
          {/* ✅ Use the new MenuFormWithUpload component */}
          <MenuFormWithUpload
            initialData={selectedMenu}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
            isEditing={isEditing}
            allowDayEdit={true}
          />
        </motion.div>
      )}

      {/* Menu Grid with FIXED SORTING - IMMER IN KORREKTER REIHENFOLGE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
        {allDays.map(day => {
          const menu = nextWeeklyMenus[day];
          const dayDisplayName = day.charAt(0).toUpperCase() + day.slice(1);

          return (
            <div
              key={day}
              className={`min-h-[300px] border-2 border-dashed rounded-2xl transition-all duration-300 ${
                dragOverDay === day ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-gray-50'
              }`}
              onDragOver={(e) => handleDragOver(e, day)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, day)}
            >
              {/* Tag-Header mit Tages-Index */}
              <div className="p-4 bg-white rounded-t-2xl border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <SafeIcon icon={FiCalendar} className="mr-2 text-blue-600" />
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-bold mr-2">
                    {allDays.indexOf(day) + 1}
                  </span>
                  {dayDisplayName}
                  <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                    Nächste Woche
                  </span>
                </h3>
              </div>

              {menu ? (
                <motion.div
                  key={menu.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white rounded-b-2xl shadow-md overflow-hidden cursor-move ${
                    draggedMenu?.id === menu.id ? 'opacity-50' : ''
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, menu)}
                >
                  <div className="relative">
                    <img src={menu.image} alt={menu.name} className="w-full h-32 object-cover" />
                    <div className="absolute top-2 right-2">
                      <div className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-xs">
                        Nächste Woche
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <h4 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                      {menu.name}
                    </h4>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {menu.description}
                    </p>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xl font-bold text-primary-600">
                        CHF {menu.price?.toFixed(2)}
                      </span>
                      {menu.formattedDate && (
                        <span className="text-xs text-gray-500">
                          {menu.formattedDate}
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      <motion.button
                        onClick={() => handleEdit(menu)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="flex-1 p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center"
                        title="Menü bearbeiten"
                      >
                        <SafeIcon icon={FiEdit2} />
                      </motion.button>

                      <motion.button
                        onClick={() => handleSwapMenus(menu)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="flex-1 p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center"
                        title="Menü verschieben"
                      >
                        <SafeIcon icon={FiMove} />
                      </motion.button>

                      <motion.button
                        onClick={() => toggleExpand(menu.id)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                        title="Details anzeigen"
                      >
                        <SafeIcon icon={expandedMenus[menu.id] ? FiChevronUp : FiChevronDown} />
                      </motion.button>
                    </div>

                    {/* Expanded Details */}
                    {expandedMenus[menu.id] && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-4 pt-4 border-t border-gray-200"
                      >
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">ID:</span>
                            <p className="text-gray-500">{menu.id}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Backend-Tag:</span>
                            <p className="text-gray-500">{menu.day}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Datum:</span>
                            <p className="text-gray-500">{menu.date || 'Nicht gesetzt'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Bild URL:</span>
                            <p className="text-gray-500 truncate">{menu.image}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ) : (
                <div className="p-4 text-center">
                  <div className="text-gray-400 mb-4">
                    <SafeIcon icon={FiPlus} className="mx-auto text-4xl mb-2" />
                    <p>Kein Menü für {dayDisplayName}</p>
                  </div>
                  <motion.button
                    onClick={handleCreate}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-primary-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-600 transition-colors"
                  >
                    Menü hinzufügen
                  </motion.button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Fallback für keine Menüs */}
      {sortedMenus.length === 0 && (
        <div className="bg-white rounded-2xl shadow-md p-8 text-center mt-8">
          <p className="text-gray-500 mb-4">Keine Menüs für nächste Woche gefunden</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={handleCopyCurrentToNext}
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <SafeIcon icon={FiCopy} />
              <span>Aktuelle Menüs kopieren</span>
            </button>
            <button
              onClick={handleCreate}
              className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700"
            >
              <SafeIcon icon={FiPlus} />
              <span>Neues Menü erstellen</span>
            </button>
          </div>
        </div>
      )}

      {/* Menu Swap Modal */}
      <MenuSwapModal
        isOpen={swapModalOpen}
        onClose={() => {
          setSwapModalOpen(false);
          setSwapSourceMenu(null);
        }}
        sourceMenu={swapSourceMenu}
        availableDays={allDays}
        existingMenus={nextWeeklyMenus}
        onSwap={executeSwap}
        isLoading={isLoading}
      />
    </div>
  );
};

export default NextWeekMenus;